import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Replace } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ReferenceSummaryCard } from './ReferenceSummaryCard';
import { useRefreshState } from './use-refresh-state';
import { useUsagePrefetch } from './use-usage-prefetch';
/**
 * The one panel shape SUP-3147 (Transfer) and SUP-3148 (Action) share.
 * Both drop a node onto the canvas with an empty reference id; the panel
 * opens the picker. Once the author attaches a reference, the panel
 * switches to the summary card. Edit opens the editing sub-view; a shared
 * reference triggers the deciding overlay first.
 */
export function ReferencePanel({ headerLabel, referenceId, reference, decidingNoun, decidingNounPlural, missingLabel, renderPicker, getSummaryTitle, renderSummaryBody, renderEditBody, onReplaceReference, onNavigateToCatalog, onRefresh, onFetchUsageCount, allowDraftDuplicate, onDuplicateImmediate, }) {
    const [view, setView] = useState({ kind: 'summary' });
    // When true, the next id-change effect lands the user in editing instead
    // of summary — set by the picker's onCreateReference path so a brand-new
    // inline row opens straight into Edit (matches the "nothing to show yet"
    // UX of a fresh action with blank fields).
    const openEditOnNextIdRef = useRef(false);
    const shouldPrefetch = Boolean(referenceId) && reference.kind === 'loaded';
    const { count: prefetchedCount, setCount: setPrefetchedCount } = useUsagePrefetch(referenceId, onFetchUsageCount, shouldPrefetch);
    const { refreshing, trigger: triggerRefresh } = useRefreshState(onRefresh);
    // Reset the sub-view machine when the attached reference id changes.
    useEffect(() => {
        if (openEditOnNextIdRef.current) {
            openEditOnNextIdRef.current = false;
            setView({ kind: 'editing', isDuplicate: false, count: 1 });
            return;
        }
        setView({ kind: 'summary' });
    }, [referenceId]);
    const isEmpty = !referenceId;
    // The editing sub-view takes over the whole aside: its own back row, body
    // padding, and sticky save footer. The outer p-4 would push the sticky
    // footer inward and clip it against the aside edge, so we drop both the
    // padding and the header while editing.
    if (view.kind === 'editing' && !isEmpty && reference.kind === 'loaded') {
        return renderEditBody({
            reference: reference.value,
            referenceId,
            isDraft: false,
            isDuplicate: view.isDuplicate,
            usageCount: view.count,
            onDone: () => setView({ kind: 'summary' }),
        });
    }
    if (view.kind === 'draft' && isEmpty) {
        return renderEditBody({
            reference: null,
            referenceId: '',
            isDraft: true,
            isDuplicate: false,
            usageCount: 1,
            onDone: () => setView({ kind: 'summary' }),
        });
    }
    return (_jsxs("div", { className: "space-y-2 p-4", children: [_jsx("p", { className: "pb-2 text-sm font-semibold", children: headerLabel }), isEmpty ? (renderPicker({
                onPickReference: onReplaceReference,
                onCreateReference: newId => {
                    openEditOnNextIdRef.current = true;
                    onReplaceReference(newId);
                },
                onStartDraft: () => setView({ kind: 'draft' }),
            })) : reference.kind === 'loading' ? (_jsxs("p", { className: "text-xs text-muted-foreground", children: ["Loading ", missingLabel, " configuration\u2026"] })) : reference.kind === 'missing' ? (_jsxs("div", { className: "space-y-2", children: [_jsxs("p", { className: "text-xs text-destructive", children: ["The referenced ", missingLabel, " was deleted."] }), _jsxs(Button, { variant: "outline", size: "sm", className: "h-8 text-xs", onClick: () => onReplaceReference(''), children: [_jsx(Replace, { className: "h-3 w-3 mr-1" }), "Pick another ", missingLabel] })] })) : (_jsx(ReferenceSummaryCard, { title: getSummaryTitle(reference.value), decidingNoun: decidingNoun, decidingNounPlural: decidingNounPlural, deciding: toDecidingState(view), onEdit: async () => {
                    if (!onFetchUsageCount) {
                        setView({ kind: 'editing', isDuplicate: false, count: 1 });
                        return;
                    }
                    let count = prefetchedCount;
                    if (count == null) {
                        setView({ kind: 'deciding', count: null, pending: true });
                        count = await onFetchUsageCount(referenceId);
                        setPrefetchedCount(count);
                    }
                    if (count <= 1) {
                        setView({ kind: 'editing', isDuplicate: false, count });
                        return;
                    }
                    setView({ kind: 'deciding', count, pending: false });
                }, onChange: () => onReplaceReference(''), onCancelDeciding: () => setView({ kind: 'summary' }), onEditForAll: () => {
                    const count = view.kind === 'deciding' ? (view.count ?? 1) : 1;
                    setView({ kind: 'editing', isDuplicate: false, count });
                }, onDuplicate: async () => {
                    const count = view.kind === 'deciding' ? (view.count ?? 1) : 1;
                    if (allowDraftDuplicate) {
                        setView({ kind: 'editing', isDuplicate: true, count });
                        return;
                    }
                    if (!onDuplicateImmediate)
                        return;
                    setView(prev => prev.kind === 'deciding'
                        ? { ...prev, pending: true }
                        : { kind: 'deciding', count: null, pending: true });
                    const newId = await onDuplicateImmediate(referenceId);
                    if (!newId) {
                        setView({ kind: 'summary' });
                        return;
                    }
                    onReplaceReference(newId);
                    // The useEffect on referenceId resets to summary; land in editing
                    // instead so the author continues on the fresh fork.
                    setView({ kind: 'editing', isDuplicate: false, count: 1 });
                }, onNavigateToCatalog: onNavigateToCatalog
                    ? () => onNavigateToCatalog(reference.value)
                    : undefined, onRefresh: triggerRefresh ? () => void triggerRefresh(referenceId) : undefined, refreshing: refreshing, children: renderSummaryBody(reference.value) }))] }));
}
function toDecidingState(view) {
    return view.kind === 'deciding'
        ? { count: view.count, pending: view.pending }
        : null;
}
//# sourceMappingURL=ReferencePanel.js.map